import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect, useCallback } from 'react';
import { PlusOutlined, SendOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import type { SuggestionChip } from './ChatWindow';
import {
  StyledInputArea,
  StyledInputCard,
  StyledNativeTextArea,
  StyledInputActions,
  StyledSendButton,
  StyledQuickActions,
  StyledQuickActionChip,
  StyledHeaderButton,
  StyledToolMenu,
  StyledToolMenuItem,
} from './index.styled';

const DEFAULT_ROWS = 2;
const MAX_ROWS = 10;

type Props = {
  onSend: (text: string) => void;
  disabled: boolean;
  placeholder?: string;
  suggestions: SuggestionChip[];
  allSuggestions: SuggestionChip[];
  hasMessages: boolean;
};

export type ChatInputHandle = {
  fillInput: (text: string) => void;
};

const ChatInput = forwardRef<ChatInputHandle, Props>(
  ({ onSend, disabled, placeholder, suggestions, allSuggestions, hasMessages }, ref) => {
    const [value, setValue] = useState('');
    const [toolMenuOpen, setToolMenuOpen] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Select the first [...] placeholder in the textarea
    const selectNextPlaceholder = useCallback((ta: HTMLTextAreaElement, startFrom = 0) => {
      const text = ta.value;
      const openIdx = text.indexOf('[', startFrom);
      if (openIdx === -1) return false;
      const closeIdx = text.indexOf(']', openIdx);
      if (closeIdx === -1) return false;
      ta.focus();
      ta.setSelectionRange(openIdx, closeIdx + 1);
      return true;
    }, []);

    useImperativeHandle(ref, () => ({
      fillInput: (text: string) => {
        setValue(text);
        // After React re-renders with new value, focus and select first placeholder
        requestAnimationFrame(() => {
          const ta = textareaRef.current;
          if (ta) selectNextPlaceholder(ta);
        });
      },
    }));

    // Measure single-line height once for max-height calculation
    const singleLineHeight = useRef<number>(0);

    useEffect(() => {
      const ta = textareaRef.current;
      if (!ta || singleLineHeight.current > 0) return;
      const saved = ta.rows;
      ta.rows = 1;
      ta.style.height = 'auto';
      singleLineHeight.current = ta.scrollHeight;
      ta.rows = saved;
      ta.style.height = '';
    }, []);

    // Auto-resize textarea: grow up to MAX_ROWS, then scroll
    const autoResize = useCallback(() => {
      const ta = textareaRef.current;
      if (!ta || !singleLineHeight.current) return;
      ta.style.height = 'auto';
      ta.style.overflowY = 'hidden';
      const maxHeight = singleLineHeight.current * MAX_ROWS;
      if (ta.scrollHeight > maxHeight) {
        ta.style.height = `${maxHeight}px`;
        ta.style.overflowY = 'auto';
      } else {
        ta.style.height = `${ta.scrollHeight}px`;
      }
    }, []);

    useEffect(() => {
      autoResize();
    }, [value, autoResize]);

    // Re-focus textarea when it becomes enabled again (e.g. after streaming ends)
    const prevDisabled = useRef(disabled);
    useEffect(() => {
      if (prevDisabled.current && !disabled) {
        textareaRef.current?.focus();
      }
      prevDisabled.current = disabled;
    }, [disabled]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
    };

    const handleSend = () => {
      const trimmed = value.trim();
      if (!trimmed || disabled) return;
      onSend(trimmed);
      setValue('');
      textareaRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === 'Tab' && !e.shiftKey) {
        const ta = textareaRef.current;
        if (ta) {
          // Search for next [...] after current cursor/selection end
          const searchFrom = ta.selectionEnd;
          if (selectNextPlaceholder(ta, searchFrom)) {
            e.preventDefault();
          } else if (selectNextPlaceholder(ta, 0)) {
            // Wrap around to beginning
            e.preventDefault();
          }
        }
      }
    };

    const handleToolClick = (s: { prompt: string; promptTemplate?: string }) => {
      setToolMenuOpen(false);
      if (!s.promptTemplate) {
        onSend(s.prompt);
        return;
      }
      setValue(s.promptTemplate);
      requestAnimationFrame(() => {
        const ta = textareaRef.current;
        if (ta) selectNextPlaceholder(ta);
      });
    };

    const showQuickActions = hasMessages && suggestions.length > 0 && !disabled;

    const toolMenuContent = (
      <StyledToolMenu>
        {allSuggestions.map((s, idx) => (
          <StyledToolMenuItem key={s.prompt} onClick={() => handleToolClick(s)}>
            <span className="tool-index">{idx + 1}</span>
            {s.label}
          </StyledToolMenuItem>
        ))}
      </StyledToolMenu>
    );

    return (
      <StyledInputArea>
        {showQuickActions && (
          <StyledQuickActions>
            {suggestions.map((s) => (
              <StyledQuickActionChip key={s.prompt} onClick={() => onSend(s.prompt)}>
                {s.label}
              </StyledQuickActionChip>
            ))}
          </StyledQuickActions>
        )}
        <StyledInputCard>
          <StyledNativeTextArea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || 'Type a message...'}
            disabled={disabled}
            aria-label="Chat input"
            rows={DEFAULT_ROWS}
          />
          <StyledInputActions>
            {allSuggestions.length > 0 && (
              <Popover
                content={toolMenuContent}
                trigger="click"
                open={toolMenuOpen}
                onOpenChange={setToolMenuOpen}
                placement="topLeft"
                arrow={false}
                overlayInnerStyle={{ padding: 4 }}
              >
                <StyledHeaderButton
                  type="text"
                  size="small"
                  icon={<PlusOutlined style={{ fontSize: 14 }} />}
                  disabled={disabled}
                  aria-label="Quick actions"
                />
              </Popover>
            )}
            <StyledSendButton
              type="primary"
              icon={<SendOutlined style={{ fontSize: 12 }} />}
              onClick={handleSend}
              disabled={!value.trim() || disabled}
              aria-label="Send message"
              size="small"
            />
          </StyledInputActions>
        </StyledInputCard>
      </StyledInputArea>
    );
  },
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
